import { Box, FormHelperText, MenuItem, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { EVENT_NAMES_BY_SOURCES, EventName, EventSource, Feature } from '@loyalty/shared-common';
import { IncomingWebhookMappedEventConfig as IncomingWebhookMappedEventConfigInterface } from '@loyalty/shared-common/event-processing-api-client';
import { QlfCodeArea } from '@qualifio/ui-commons/dist/components/qlf-code-area';
import { QlfFormRow } from '@qualifio/ui-commons';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import {
  useEventSourceNames,
  useEventSourceValuesValue,
} from '../../../../rules/rule-table-constants';
import { useFeatures } from '../../../../features/FeaturesContext';
import { validateInteractionJsonata, validateJsonata } from '../../../incoming-webhook-utils';

const allowedMapToSources: EventSource[] = [EventSource.ECOMMERCE, EventSource.BINARY];

export const DEFAULT_INTERACTION_JSONATA = `{
  "externalId": id,
  "data": data,
  "label": "Ticket receipt",
  "status": "Pending"
}`;

export const DEFAULT_MAPPED_EVENT_CONFIG: IncomingWebhookMappedEventConfigInterface = {
  mapTo: {
    source: EventSource.BINARY,
    name: EventName.BASIC,
  },
  jsonata: `{
  "clientEventId": id,
  "profile": {
    "kind": "email",
    "value": user.email
  },
  "payload": {
  "timestamp": date,
    "tags": [
      {"name": "example tag", "value": "example"}
    ],
    "counters": [
      {"name": "example counter", "value": 0}
    ]
  }
}`,
  interactionJsonata: DEFAULT_INTERACTION_JSONATA,
};

const useStyles = makeStyles(() => ({
  jsonataContainer: {
    height: 300,
  },
}));

export function IncomingWebhookMappedEventConfig() {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();
  const { features } = useFeatures();
  const { write: webhookWrite } = features[Feature.INCOMING_WEBHOOK];
  const { read: interactionEnabled } = features[Feature.INTERACTION];

  const eventSourceNames = useEventSourceNames();
  const mapToSource = watch('config.mapTo.source');
  const sourceTranslationEventName = useEventSourceValuesValue();
  const integrationId = watch('config.integrationId');
  const interactionId = watch('config.interactionId');

  const jsonata = watch('config.jsonata');
  const { valid: jsonataIsValid, error: jsonataError } = jsonata
    ? validateJsonata(jsonata)
    : { valid: true, error: undefined };
  const interactionJsonata = watch('config.interactionJsonata');
  const shouldValidateInteractionJsonata = interactionEnabled && !integrationId && !!interactionId;
  const { valid: interactionJsonataIsValid, error: interactionJsonataError } =
    shouldValidateInteractionJsonata
      ? validateInteractionJsonata(interactionJsonata || '')
      : { valid: true, error: undefined };

  return (
    <>
      <Controller
        control={control}
        name="config.mapTo.source"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <QlfFormRow label={t('earning-rules.properties.eventSource')}>
            <TextField
              {...field}
              disabled={!webhookWrite}
              inputRef={ref}
              error={!!error}
              helperText={error?.message}
              select
            >
              {allowedMapToSources.map(value => (
                <MenuItem key={value} value={value}>
                  {eventSourceNames[value]}
                </MenuItem>
              ))}
            </TextField>
          </QlfFormRow>
        )}
      />

      <Controller
        control={control}
        name="config.mapTo.name"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <QlfFormRow label={t('earning-rules.properties.eventName')}>
            <TextField
              {...field}
              disabled={!webhookWrite}
              inputRef={ref}
              error={!!error}
              helperText={error?.message}
              select
            >
              {EVENT_NAMES_BY_SOURCES[mapToSource]?.map(value => (
                <MenuItem key={value} value={value}>
                  {sourceTranslationEventName[value]}
                </MenuItem>
              ))}
            </TextField>
          </QlfFormRow>
        )}
      />

      {!integrationId ? (
        <Controller
          control={control}
          name="config.jsonata"
          defaultValue=""
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <QlfFormRow label={t('incoming-webhooks.inputs.config.jsonata')}>
              <Box className={classes.jsonataContainer}>
                <QlfCodeArea {...field} />
                {(!!error || !jsonataIsValid) && (
                  <FormHelperText error>{error?.message || jsonataError}</FormHelperText>
                )}
              </Box>
            </QlfFormRow>
          )}
        />
      ) : null}

      {shouldValidateInteractionJsonata ? (
        <Controller
          control={control}
          name="config.interactionJsonata"
          defaultValue=""
          rules={{
            validate: value => {
              if (!shouldValidateInteractionJsonata) {
                return true;
              }

              const validation = validateInteractionJsonata(value || '');
              return validation.valid || validation.error;
            },
          }}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <QlfFormRow label={t('incoming-webhooks.inputs.config.interaction-jsonata')}>
              <Box className={classes.jsonataContainer}>
                <QlfCodeArea {...field} />
                {(!!error || !interactionJsonataIsValid) && (
                  <FormHelperText error>{error?.message || interactionJsonataError}</FormHelperText>
                )}
              </Box>
            </QlfFormRow>
          )}
        />
      ) : null}
    </>
  );
}
